import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  checkAuth,
  getAccountData,
  getCurrentUser,
  invalidateCurrentSubscriptionCache,
  invalidateCurrentUserCache,
  invalidatePaymentsCache,
  loginUser,
  logoutUser,
  registerUser,
} from "@/service";
import { AuthUser, AuthAccount, RegisterRequest, UserRole } from "@/types/auth";
import { isPublicOnlyPath, isPublicPath } from "@/routes/paths";

const ORPHAN_STORAGE_KEY = "yuha_orphan";

interface AuthContextValue {
  loggedIn: boolean;
  loading: boolean;
  user: AuthUser | null;
  account: AuthAccount | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: RegisterRequest) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return context;
};

// Usuário sem conta ativa ("órfão"): o service grava a flag e o app manda
// para a contratação de conta.
const consumeOrphanFlag = (): boolean => {
  try {
    if (!localStorage.getItem(ORPHAN_STORAGE_KEY)) return false;
    localStorage.removeItem(ORPHAN_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [account, setAccount] = useState<AuthAccount | null>(null);

  useEffect(() => {
    const pathname = window.location.pathname;
    // Telas de entrada (login, cadastro, parceiros, senha): quem já tem
    // sessão ativa é redirecionado para o painel pelo PublicOnlyRoute; para o
    // visitante anônimo, um 401 aqui é esperado e não deve virar toast/redirect.
    const isEntry = isPublicOnlyPath(pathname);

    // Demais páginas públicas (vitrine, callback de OAuth...) não verificam sessão.
    if (!isEntry && isPublicPath(pathname)) {
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        if (!(await checkAuth(isEntry))) {
          if (!isEntry) window.location.href = "/signin/";
          return;
        }

        const [currentUser, accountData] = await Promise.all([
          getCurrentUser().catch(() => null),
          getAccountData().catch(() => null),
        ]);

        if (currentUser) {
          setUser({
            id: currentUser.id,
            accountId: currentUser.accountId,
            name: currentUser.name,
            email: currentUser.email,
            roles: currentUser.roles as UserRole[],
          });
        }
        if (accountData) {
          setAccount({
            accountId: currentUser?.accountId,
            name: accountData.name,
            domain: accountData.domain,
          });
        }
        // Marcado por último para já entrar com user/account preenchidos
        // quando o PublicOnlyRoute/ProtectedRoute liberar a tela.
        setLoggedIn(true);

        if (consumeOrphanFlag()) {
          window.location.href = "/dash/contratar-conta/";
        }
      } catch {
        if (!isEntry) window.location.href = "/signin/";
      } finally {
        setLoading(false);
      }
    };

    void verify();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await loginUser({ email, password });
    if (!response.success || !response.user) return false;

    setLoggedIn(true);
    setUser(response.user);
    if (response.account) setAccount(response.account);
    if (response.isOrphan) window.location.href = "/dash/contratar-conta/";
    return true;
  }, []);

  // O cadastro não autentica: o fluxo segue para o checkout.
  const register = useCallback(async (data: RegisterRequest) => {
    const response = await registerUser(data);
    return response.success;
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } finally {
      invalidateCurrentUserCache();
      invalidateCurrentSubscriptionCache();
      invalidatePaymentsCache();
      setLoggedIn(false);
      setUser(null);
      setAccount(null);
      // O backend limpa os cookies HttpOnly.
      window.location.href = "/signin/";
    }
  }, []);

  const value = useMemo(
    () => ({ loggedIn, loading, user, account, login, register, logout }),
    [loggedIn, loading, user, account, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
